<template>
  <div
    id="calendar-grid"
    ref="calendarRef"
    class="calendar relative"
    role="grid"
    aria-labelledby="calendar-title"
    aria-describedby="calendar-instructions"
  >
    <div id="calendar-instructions" class="sr-only">
      Use arrow keys to navigate between dates. Press Enter or Space to select a date. Use Page Up
      and Page Down to change months.
    </div>

    <div aria-live="polite" aria-atomic="true" class="sr-only">
      {{ liveRegionText }}
    </div>

    <div class="mb-4 flex items-center justify-start">
      <button
        type="button"
        class="calendar-prev rounded p-4 disabled:pointer-events-none disabled:opacity-70 md:border md:border-linear-strong md:guest-ui:border-linear-g-primary hover:md:bg-surface-secondary focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2"
        aria-label="Go to previous month"
        :disabled="disabled || isPrevMonthDisabled"
        @click="previousMonth"
      >
        <span aria-hidden="true" class="inline-block size-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </span>
      </button>
      <h3
        id="calendar-title"
        class="calendar-title text-xl text-center font-heading font-semibold uppercase w-full inline-block"
      >
        {{ monthTitle }}
      </h3>
      <button
        type="button"
        class="calendar-next rounded p-4 disabled:pointer-events-none disabled:opacity-70 md:border md:border-linear-strong md:guest-ui:border-linear-g-primary hover:md:bg-surface-secondary focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2"
        aria-label="Go to next month"
        :disabled="disabled || isNextMonthDisabled"
        @click="nextMonth"
      >
        <span aria-hidden="true" class="inline-block size-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </span>
      </button>
    </div>

    <div class="flex border-b border-linear-strong guest-ui:border-linear-g-primary" role="row">
      <div
        v-for="(label, index) in weekLabels"
        :key="`weeklabel-${label}`"
        role="columnheader"
        :aria-label="weekLabelsFull[index]"
        class="calendar-week-label flex-1 border-r border-linear-strong px-3 py-2 text-center text-sm font-medium text-fg-tertiary last:border-r-0 guest-ui:border-linear-g-primary"
      >
        <span aria-hidden="true">{{ label }}</span>
      </div>
    </div>

    <div v-for="week in weeks" :key="week.key" class="flex" role="row">
      <button
        v-for="day in week.days"
        :key="day.date"
        type="button"
        role="gridcell"
        :aria-pressed="day.isSelected"
        :aria-label="getDayAriaLabel(day)"
        :aria-disabled="disabled || !day.inMonth"
        :disabled="disabled || !day.inMonth"
        class="calendar-day flex-1 border-r border-b border-linear-strong px-2 py-3 text-center last:border-r-0 disabled:pointer-events-none disabled:opacity-70 guest-ui:border-linear-g-primary focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2"
        :class="{
          'calendar-day-outside': !day.inMonth,
          'calendar-day-inside': day.inMonth,
          'calendar-day-selected': day.isSelected,
          'calendar-day-highlighted': day.isHighlighted && !day.isSelected,
          'cursor-default text-fg-tertiary': !day.inMonth,
          'cursor-pointer': day.inMonth,
          'bg-calendar-bg-active text-calendar-fg-active venue-ui:!bg-surface-secondary':
            day.isSelected,
          'hover:bg-surface-secondary': !day.isSelected && day.inMonth,
          'text-button-guest-primary underline': day.isHighlighted && !day.isSelected,
        }"
        @click="clickDay(day)"
      >
        <span aria-hidden="true">{{ day.day }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Moment } from 'moment'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import { useSettingStore } from '@/stores/setting'
import { RovingTabindexGrid } from '@/utils/rovingTabindexGrid'

interface CalendarDay {
  date: string
  day: string
  inMonth: boolean
  isSelected: boolean
  isToday: boolean
  isHighlighted: boolean
}

interface CalendarWeek {
  key: string
  days: CalendarDay[]
}

const props = withDefaults(
  defineProps<{
    modelValue?: string
    datesToHighlight?: string[]
    disabled?: boolean
    minDate?: string
    maxDate?: string
  }>(),
  { disabled: false },
)

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars,no-undef
  (e: 'update:modelValue', value: string): void
}>()

const settingStore = useSettingStore()
const parseCalendarDate = (datetime: string): Moment => settingStore.parseMoment(datetime)
const nowCalendarDate = (): Moment => parseCalendarDate(settingStore.nowDateTime())

const initialDate = props.modelValue ? parseCalendarDate(props.modelValue) : nowCalendarDate()
const currentYear = ref(initialDate.year())
const currentMonth = ref(initialDate.month() + 1)

const weekLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const weekLabelsFull = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const datePrefix = computed(() => {
  const month = currentMonth.value < 10 ? `0${currentMonth.value}` : currentMonth.value
  return `${currentYear.value}-${month}`
})

const firstOfTheMonth = computed(() => parseCalendarDate(`${datePrefix.value}-01`))

const monthTitle = computed(() => firstOfTheMonth.value.format('MMMM YYYY'))

const startDay = computed(() => firstOfTheMonth.value.clone().startOf('week'))

const endDay = computed(() => firstOfTheMonth.value.clone().endOf('month').endOf('week'))

const today = computed(() => settingStore.dateIsoStr(settingStore.nowDateTime()))

const isPrevMonthDisabled = computed(() => {
  if (!props.minDate) return false
  const prev = firstOfTheMonth.value.clone().subtract(1, 'month')
  return prev.endOf('month').isBefore(parseCalendarDate(props.minDate), 'day')
})

const isNextMonthDisabled = computed(() => {
  if (!props.maxDate) return false
  const next = firstOfTheMonth.value.clone().add(1, 'month')
  return next.startOf('month').isAfter(parseCalendarDate(props.maxDate), 'day')
})

const calendarDays = computed(() => {
  const days: CalendarDay[] = []
  const date: Moment = startDay.value.clone().subtract(1, 'day')

  while (endDay.value.isAfter(date, 'day')) {
    const momentDay: Moment = date.add(1, 'day').clone()
    const dateStr = momentDay.format('YYYY-MM-DD')

    days.push({
      date: dateStr,
      day: momentDay.format('D'),
      inMonth: dateStr.startsWith(datePrefix.value),
      isSelected: dateStr === props.modelValue,
      isToday: dateStr === today.value,
      isHighlighted: props.datesToHighlight?.includes(dateStr) ?? false,
    })
  }
  return days
})

const weeks = computed(() => {
  return calendarDays.value.reduce((results: CalendarWeek[], item, index) => {
    const weekIndex = Math.floor(index / 7)

    if (!results[weekIndex]) {
      results[weekIndex] = {
        days: [],
        key: `week-${item.date}`,
      }
    }
    results[weekIndex].days.push(item)

    return results
  }, [])
})

const liveRegionText = ref('')

function getDayAriaLabel(day: CalendarDay): string {
  const momentDate = parseCalendarDate(day.date)
  const dayName = momentDate.format('dddd')
  const fullDate = momentDate.format('MMMM D, YYYY')

  let label = `${dayName}, ${fullDate}`

  if (day.isToday) {
    label += ', Today'
  }

  if (day.isSelected) {
    label += ', Selected'
  }

  if (day.isHighlighted && !day.isSelected) {
    label += ', Has performances'
  }

  if (!day.inMonth) {
    label += ', Not in current month'
  }

  return label
}

function clickDay(day: CalendarDay) {
  if (props.disabled) return
  if (!day.inMonth) return

  if (props.modelValue === day.date) {
    emit('update:modelValue', '')
    liveRegionText.value = `Date ${parseCalendarDate(day.date).format('MMMM D')} deselected`
  } else {
    emit('update:modelValue', day.date)
    liveRegionText.value = `Date ${parseCalendarDate(day.date).format('MMMM D')} selected`
  }
}

function previousMonth() {
  if (props.disabled || isPrevMonthDisabled.value) return
  if (currentMonth.value === 1) {
    currentMonth.value = 12
    currentYear.value--
  } else {
    currentMonth.value--
  }
  announceMonthChange()
}

function nextMonth() {
  if (props.disabled || isNextMonthDisabled.value) return
  if (currentMonth.value === 12) {
    currentMonth.value = 1
    currentYear.value++
  } else {
    currentMonth.value++
  }
  announceMonthChange()
}

function announceMonthChange() {
  nextTick(() => {
    liveRegionText.value = `Viewing ${monthTitle.value}`
  })
}

const calendarRef = ref<HTMLElement | null>(null)
let rovingTabindexGrid: RovingTabindexGrid | null = null
let isKeyboardNavigation = false

function handleMonthChange(direction: 'prev' | 'next' | 'prevPage' | 'nextPage') {
  if (direction === 'prev' || direction === 'prevPage') {
    if (isPrevMonthDisabled.value) return
    isKeyboardNavigation = true
    previousMonth()
  } else {
    if (isNextMonthDisabled.value) return
    isKeyboardNavigation = true
    nextMonth()
  }

  nextTick(() => {
    const focusPosition = direction === 'prev' ? 'last' : 'first'
    rovingTabindexGrid?.initialize(focusPosition)
    isKeyboardNavigation = false
  })
}

onMounted(() => {
  if (calendarRef.value) {
    rovingTabindexGrid = new RovingTabindexGrid(calendarRef.value, 7, handleMonthChange)
  }
})

onUnmounted(() => {
  rovingTabindexGrid?.destroy()
  rovingTabindexGrid = null
})

watch([currentMonth, currentYear], () => {
  if (!isKeyboardNavigation) {
    nextTick(() => {
      rovingTabindexGrid?.initialize('none')
    })
  }
})
</script>
